import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { compressImage, generateThumbnail, validateImageFile } from '../utils/photoUtils';

interface UsePhotoUploadOptions {
  maxPhotos?: number;
  maxSizePerPhoto?: number; // in MB
  compressImages?: boolean;
  generateThumbnails?: boolean;
}

interface UploadProgress {
  photoIndex: number;
  progress: number;
  status: 'uploading' | 'processing' | 'complete' | 'error';
}

export const usePhotoUpload = (options: UsePhotoUploadOptions = {}) => {
  const {
    maxPhotos = 5,
    maxSizePerPhoto = 10,
    compressImages = true,
    generateThumbnails = true
  } = options;

  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [error, setError] = useState<string | null>(null);

  const uploadPhotos = async (
    files: File[],
    metadata: {
      title?: string;
      description?: string;
      category: string;
      tags: string[];
      location?: string;
      visibility: string;
    }
  ): Promise<string[]> => {
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    // Validate files
    for (const file of files) {
      const validation = validateImageFile(file, maxSizePerPhoto * 1024 * 1024);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }
    }

    if (files.length > maxPhotos) {
      throw new Error(`Máximo ${maxPhotos} fotos permitidas`);
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(files.map((_, index) => ({
      photoIndex: index,
      progress: 0,
      status: 'uploading'
    })));

    try {
      const uploadedUrls: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Update progress
        setUploadProgress(prev => prev.map(p => 
          p.photoIndex === i ? { ...p, status: 'processing' } : p
        ));

        let processedFile = file;
        
        // Compress image if enabled
        if (compressImages) {
          try {
            processedFile = await compressImage(file);
          } catch (compressionError) {
            console.warn('Image compression failed, using original:', compressionError);
          }
        }

        // Generate unique filename
        const fileExt = processedFile.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}-${i}.${fileExt}`;

        // Check if Supabase is configured
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        
        if (!supabaseUrl || !supabaseKey || 
            supabaseUrl.includes('placeholder') || supabaseKey.includes('placeholder') ||
            supabaseUrl === 'your_supabase_project_url' || supabaseKey === 'your_supabase_anon_key' ||
            supabaseUrl.includes('your-project') || supabaseKey.includes('your-actual')) {
          // Demo mode - create mock URL
          const mockUrl = URL.createObjectURL(processedFile);
          uploadedUrls.push(mockUrl);
          
          setUploadProgress(prev => prev.map(p => 
            p.photoIndex === i ? { ...p, progress: 100, status: 'complete' } : p
          ));
          continue;
        }

        // Upload to Supabase Storage
        const { data, error: uploadError } = await supabase.storage
          .from('photos')
          .upload(fileName, processedFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          throw new Error(`Error subiendo foto ${i + 1}: ${uploadError.message}`);
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('photos')
          .getPublicUrl(fileName);

        uploadedUrls.push(publicUrl);

        // Update progress
        setUploadProgress(prev => prev.map(p => 
          p.photoIndex === i ? { ...p, progress: 100, status: 'complete' } : p
        ));
      }

      // Create media items in database
      if (supabaseUrl && !supabaseUrl.includes('placeholder')) {
        const mediaItems = uploadedUrls.map((url, index) => ({
          user_id: user.id,
          type: 'image' as const,
          url,
          title: metadata.title || `Foto ${index + 1}`,
          description: metadata.description,
          category: metadata.category,
          tags: metadata.tags,
          visibility: metadata.visibility
        }));

        const { error: dbError } = await supabase
          .from('media_items')
          .insert(mediaItems);

        if (dbError) {
          console.error('Error saving to database:', dbError);
          // Don't throw here, photos are already uploaded
        }
      }

      return uploadedUrls;

    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const resetUpload = () => {
    setUploadProgress([]);
    setError(null);
  };

  return {
    uploadPhotos,
    isUploading,
    uploadProgress,
    error,
    resetUpload
  };
};